import { Button, Dialog, FieldLabelText, Message } from 'Components/Core';
import { Table } from 'Components/Layout';
import { WorksheetEditorInjectedPropsProvider } from 'Components/Worksheets/Core';
import { getConflictData } from 'Data/Helpers/worksheetHelpers';
import { actions } from 'Data/ReduxViewModels/WorksheetEditor';
import deepEquals from 'Lib/Object/deepEquals';
import moment from 'moment';
import React from 'react';
import { FaExclamationTriangle } from 'react-icons/fa';
import { useDispatch, useSelector } from 'react-redux';

// Contents component is separate so changing the sync model doesn't cause this to render repeatedly.
const WorksheetEditorConflictResolutionDialogContents: React.FC = () => {
  const conflictData = useSelector((state) => getConflictData(state.worksheetEditor), deepEquals);

  return (
    <section
      style={{
        display: 'grid',
        rowGap: '10px',
      }}
    >
      <Message warning>
        <FaExclamationTriangle size="24" style={{ marginBottom: '-4px' }} />
        {' The following users have made changes: '}
        {conflictData.syncConflictUsers &&
          conflictData.syncConflictUsers?.map((user, i) => (
            <span key={i}>
              {i > 0 ? ', ' : ''}
              <strong>{user.Name}</strong>
              <time dateTime={user.Timestamp}>
                <em>{user.Timestamp && ` (${moment(user.Timestamp).fromNow()})`}</em>
              </time>
            </span>
          ))}
      </Message>
      These conflicting changes were encountered:
      <Table
        colHeaders={['Field', 'Your Value', 'Their Value']}
        colWidths={[250]}
        data={Object.entries(conflictData.syncConflictFields).map(([k, v]) => [
          <FieldLabelText name={k} />,
          v.clientValue,
          v.serverValue,
        ])}
        alternateRowShading={true}
      />
      <span>Do you want to save your changes or discard them?</span>
    </section>
  );
};

/**
 * Displays conflict resolution information for the worksheet editor
 */
const WorksheetEditorConflictResolutionDialog: React.FC = () => {
  const unresolvedConflict = useSelector((s) => s.worksheetEditor.syncStatus === 'unresolvedConflict');
  const dispatch = useDispatch();

  return (
    <WorksheetEditorInjectedPropsProvider>
      <Dialog
        title="Conflict Encountered"
        alwaysEnabled
        visible={unresolvedConflict}
        width="500px"
        buttons={[
          <Button key="1" onClick={() => dispatch(actions.syncConflictResolve('client'))}>
            Save Changes
          </Button>,
          <Button key="2" onClick={() => dispatch(actions.syncConflictResolve('server'))}>
            Discard Changes
          </Button>,
        ]}
      >
        {unresolvedConflict && <WorksheetEditorConflictResolutionDialogContents />}
      </Dialog>
    </WorksheetEditorInjectedPropsProvider>
  );
};

export default WorksheetEditorConflictResolutionDialog;
